import { useEffect, useRef, useState } from 'react'
import { rfStore } from '../lib/rfstore'
import type { BandScope } from '../lib/protocol'
import { useI18n } from '../lib/i18n'
import type { pt } from '../lib/i18n/pt'
import { corDoModo, nivelParaCor, nivelParaRGB } from '../lib/sinalCores'

/**
 * Cascata/panorama.
 *
 * O servidor manda os SINAIS presentes na janela, não o espectro amostrado.
 * Enviar 256 bins a 10 Hz custaria ~7 KB/s por usuário; enviar meia dúzia de
 * sinais custa dezenas de bytes e o cliente sintetiza o resto. É a mesma
 * decisão do áudio (docs/05-audio-dsp-wasm.md §1).
 *
 * Isso é o que torna o ZOOM inteiramente do lado do cliente: cada sinal
 * carrega frequência/largura exatas, não bins pré-quantizados, então
 * "aproximar" é só chamar a MESMA síntese com um span menor — as gaussianas
 * recalculam na frequência certa, não reamostram um buffer discreto. Não dá
 * pra AFASTAR além do span que o servidor manda, porque não existe sinal
 * nenhum fora dessa janela pra mostrar.
 *
 * Renderiza em canvas 2D com rolagem por drawImage. A decisão documentada em
 * docs/06-frontend-ux-mobile.md era WebGL2 — adiada de propósito: a 256 bins
 * o caminho 2D custa menos de 1 ms por quadro, e WebGL só se paga quando a
 * contagem de bins subir para uma visão de banda inteira.
 *
 * Como todo componente do caminho quente, renderiza UMA vez no React e depois
 * se atualiza sozinho por requestAnimationFrame lendo o store externo.
 */

const BINS = 256
const ROWS = 140
/** Faixa dinâmica desenhada, em dB acima do piso de ruído. */
const DYNAMIC_RANGE = 60
/** Altura da linha de espectro ao vivo — o traço "de agora", separado do
 * histórico que rola embaixo. */
const LINE_H = 36

/** Passos de zoom, do span cheio que o servidor manda até o mais próximo. */
const ZOOM_NIVEIS = [20, 10, 5, 2] as const

export function Waterfall({
  height = 140,
  onTune,
}: {
  height?: number
  /** Tocar num ponto da cascata sintoniza ali. Omitido = só visual. */
  onTune?: (freqKHz: number) => void
}) {
  const { t } = useI18n()
  const ref = useRef<HTMLCanvasElement>(null)
  const lineRef = useRef<HTMLCanvasElement>(null)
  const rulerRef = useRef<HTMLDivElement>(null)
  const metaRef = useRef<HTMLSpanElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const [zoomKHz, setZoomKHz] = useState<(typeof ZOOM_NIVEIS)[number]>(ZOOM_NIVEIS[0])
  // O laço de desenho é montado UMA vez (abaixo) e não pode reler estado do
  // React a cada quadro — por isso o zoom viaja por ref, não pelo valor
  // capturado no fechamento da primeira renderização.
  const zoomRef = useRef<number>(zoomKHz)
  useEffect(() => {
    zoomRef.current = zoomKHz
  }, [zoomKHz])

  useEffect(() => {
    const canvas = ref.current
    const lineCanvas = lineRef.current
    if (!canvas || !lineCanvas) return
    const ctx = canvas.getContext('2d', { alpha: false })
    const lineCtx = lineCanvas.getContext('2d')
    if (!ctx || !lineCtx) return

    canvas.width = BINS
    canvas.height = ROWS
    ctx.fillStyle = '#0d1014'
    ctx.fillRect(0, 0, BINS, ROWS)

    lineCanvas.width = BINS
    lineCanvas.height = LINE_H

    const row = ctx.createImageData(BINS, 1)
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let raf = 0
    let last = 0

    const draw = (ts: number) => {
      raf = requestAnimationFrame(draw)
      // ~20 linhas por segundo. Mais que isso o olho não distingue e só gasta.
      if (ts - last < 50) return
      last = ts

      const scope = rfStore.snapshot().scope
      // Nunca maior que o span que o servidor de fato mandou — não existe
      // sinal fora dessa janela pra sintetizar.
      const spanKHz = Math.min(zoomRef.current, scope?.span_khz ?? ZOOM_NIVEIS[0])
      const levels = computeLevels(scope, spanKHz, reduce)

      // Rola tudo uma linha para baixo e desenha a nova no topo.
      ctx.drawImage(canvas, 0, 0, BINS, ROWS - 1, 0, 1, BINS, ROWS - 1)
      paintRow(row.data, levels)
      ctx.putImageData(row, 0, 0)

      paintLine(lineCtx, levels)

      // Texto, régua e lista saem do MESMO laço. O store não notifica o
      // React em mudança de scope — é caminho quente — então pôr isto em
      // JSX faria a leitura congelar no último render, que foi exatamente
      // o bug daqui.
      paintMeta(scope, spanKHz, metaRef.current, rulerRef.current, listRef.current, t.waterfall)
    }

    raf = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(raf)
  }, [t])

  const proximoZoom = () => {
    const i = ZOOM_NIVEIS.indexOf(zoomKHz)
    setZoomKHz(ZOOM_NIVEIS[(i + 1) % ZOOM_NIVEIS.length] ?? ZOOM_NIVEIS[0])
  }

  const tocarPraSintonizar = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onTune) return
    const scope = rfStore.snapshot().scope
    if (!scope) return
    const spanKHz = Math.min(zoomRef.current, scope.span_khz)
    const rect = e.currentTarget.getBoundingClientRect()
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    onTune(scope.center_khz - spanKHz / 2 + frac * spanKHz)
  }

  return (
    <>
      <canvas
        ref={lineRef}
        className="wf-linha"
        style={{ width: '100%', height: LINE_H, display: 'block' }}
        onClick={tocarPraSintonizar}
      />
      <div className="waterfall">
        <canvas
          ref={ref}
          style={{ width: '100%', height, display: 'block' }}
          onClick={tocarPraSintonizar}
        />
        <div className="wf-cursor" />
        <button
          type="button"
          className="wf-zoom"
          onClick={proximoZoom}
          aria-label={`${t.waterfall.zoom} ${zoomKHz} kHz ${t.waterfall.toqueParaTrocar}`}
        >
          {zoomKHz}k
        </button>
      </div>
      <div className="scope-ruler" ref={rulerRef} />
      <div className="scope-meta">
        <span ref={metaRef}>{t.waterfall.semSinal}</span>
      </div>
      <ul className="scope-list" ref={listRef} />
    </>
  )
}

/**
 * Nível (dB acima do piso) de cada um dos 256 bins — piso de ruído com
 * textura aleatória, mais uma gaussiana por sinal com largura proporcional
 * à banda ocupada. É o que faz CW virar risco fino e FM virar bloco largo,
 * exatamente como num receptor de verdade.
 *
 * Parametrizada pelo span EFETIVO (com zoom aplicado), não o span cru que o
 * servidor manda — é essa a peça inteira que faz o zoom funcionar sem pedir
 * mais dados: reamostra a MESMA lista de sinais numa janela mais estreita.
 * Reaproveitada pela cascata (cor) e pela linha ao vivo (altura).
 */
function computeLevels(scope: BandScope | null, spanKHz: number, reduce: boolean): Float32Array {
  const out = new Float32Array(BINS)
  const spanHz = spanKHz * 1000
  const centerHz = (scope?.center_khz ?? 0) * 1000
  const floor = scope?.noise_floor_dbm ?? -110
  const hzPerBin = spanHz / BINS

  for (let x = 0; x < BINS; x++) {
    // Nunca há silêncio absoluto num receptor: o chiado de fundo é obrigatório.
    let dB = reduce ? 2 : Math.random() * 5

    if (scope) {
      const binHz = centerHz + (x - BINS / 2) * hzPerBin
      for (const s of scope.signals ?? []) {
        const w = Math.max(s.width_hz, hzPerBin) / 2
        const d = (s.freq_khz * 1000 - binHz) / w
        if (d > 4 || d < -4) continue
        const above = s.level_dbm - floor
        if (above <= 0) continue
        dB += above * Math.exp(-0.5 * d * d)
      }
    }
    out[x] = dB
  }
  return out
}

/** Pinta uma linha do histórico a partir dos níveis já calculados. */
function paintRow(px: Uint8ClampedArray, levels: Float32Array): void {
  for (let x = 0; x < BINS; x++) {
    const v = Math.max(0, Math.min(1, (levels[x] ?? 0) / DYNAMIC_RANGE))
    const i = x * 4
    const [r, g, b] = nivelParaRGB(v)
    px[i] = r
    px[i + 1] = g
    px[i + 2] = b
    px[i + 3] = 255
  }
}

/**
 * Traço do painel — o nível "de agora", ao contrário do histórico que rola.
 * Redesenha inteiro a cada quadro em vez de rolar: são só 256 pontos, custo
 * desprezível, e é o visual clássico de painel de SDR (linha + preenchimento
 * embaixo), separado da cascata de histórico.
 */
function paintLine(ctx: CanvasRenderingContext2D, levels: Float32Array): void {
  ctx.clearRect(0, 0, BINS, LINE_H)
  ctx.beginPath()
  for (let x = 0; x < BINS; x++) {
    const v = Math.max(0, Math.min(1, (levels[x] ?? 0) / DYNAMIC_RANGE))
    const y = LINE_H - v * LINE_H
    if (x === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  }
  ctx.strokeStyle = 'rgb(224, 145, 58)'
  ctx.lineWidth = 1
  ctx.stroke()
  ctx.lineTo(BINS, LINE_H)
  ctx.lineTo(0, LINE_H)
  ctx.closePath()
  ctx.fillStyle = 'rgba(224, 145, 58, 0.18)'
  ctx.fill()
}

/** Degraus "redondos" pra régua — evita marcação em 1.37 kHz. */
const PASSOS_REGUA = [0.5, 1, 2, 2.5, 5, 10, 20] as const

function passoDaRegua(spanKHz: number): number {
  // Mira ~5 marcações visíveis, qualquer que seja o zoom.
  const alvo = spanKHz / 5
  let melhor: number = PASSOS_REGUA[0]
  for (const p of PASSOS_REGUA) {
    if (Math.abs(p - alvo) < Math.abs(melhor - alvo)) melhor = p
  }
  return melhor
}

/** Marcações da régua, em porcentagem da largura — mesma técnica que
 * .wf-cursor já usa pra ficar centralizado em qualquer largura responsiva,
 * sem depender de pixel exato do canvas. */
function marcacoesDaRegua(centerKHz: number, spanKHz: number): { pct: number; label: string }[] {
  const passo = passoDaRegua(spanKHz)
  const lo = centerKHz - spanKHz / 2
  const hi = centerKHz + spanKHz / 2
  const primeira = Math.ceil(lo / passo) * passo
  const out: { pct: number; label: string }[] = []
  for (let f = primeira; f <= hi + 1e-6; f += passo) {
    out.push({ pct: ((f - lo) / spanKHz) * 100, label: f.toFixed(passo < 1 ? 1 : 0) })
  }
  return out
}

/** Atualiza o status, a régua e a lista de sinais direto no DOM, fora do
 * React (ver o comentário no laço de desenho sobre por que). */
function paintMeta(
  scope: BandScope | null,
  spanKHz: number,
  meta: HTMLElement | null,
  ruler: HTMLDivElement | null,
  list: HTMLUListElement | null,
  tr: Record<keyof typeof pt.waterfall, string>,
): void {
  const sigs = (scope?.signals ?? []).slice().sort((a, b) => b.level_dbm - a.level_dbm)

  if (meta) {
    meta.textContent = !scope
      ? tr.semSinal
      : sigs.length
        ? `${sigs.length} ${sigs.length > 1 ? tr.sinais : tr.sinal} · ${tr.piso} ${scope.noise_floor_dbm.toFixed(0)} dBm`
        : `${tr.bandaSilenciosa} · ${tr.piso} ${scope.noise_floor_dbm.toFixed(0)} dBm`
  }

  if (ruler && scope) {
    const key = `${scope.center_khz.toFixed(2)}@${spanKHz}`
    if (ruler.dataset['key'] !== key) {
      ruler.dataset['key'] = key
      ruler.textContent = ''
      for (const tick of marcacoesDaRegua(scope.center_khz, spanKHz)) {
        const span = document.createElement('span')
        span.className = 'scope-tick'
        span.style.left = `${tick.pct}%`
        span.textContent = tick.label
        ruler.append(span)
      }
    }
  }

  if (!list) return

  // TODOS os sinais da janela, não só os mais fortes — é o que faz a lista
  // servir pra localizar quem está por perto, não só quem está mais claro.
  // Reconstrói só quando o conjunto muda, para não recriar nós 20× por segundo.
  const key = sigs.map((s) => `${s.callsign}@${s.freq_khz}:${s.level_dbm.toFixed(0)}`).join('|')
  if (list.dataset['key'] === key) return
  list.dataset['key'] = key

  const floor = scope?.noise_floor_dbm ?? -110

  list.textContent = ''
  for (const s of sigs) {
    const li = document.createElement('li')
    const tag = document.createElement('span')
    tag.className = s.beacon ? 'pill on' : 'pill'
    tag.textContent = s.beacon ? tr.farol : s.mode
    // Farol já tem tratamento sólido âmbar (.pill on); os demais ganham a
    // cor da própria modulação, pra distinguir FM/CW/digital/SSB na lista.
    if (!s.beacon) tag.style.color = corDoModo(s.mode)
    const cs = document.createElement('strong')
    cs.textContent = s.callsign
    const grow = document.createElement('span')
    grow.className = 'grow'
    const f = document.createElement('span')
    f.textContent = `${s.freq_khz.toFixed(1)} kHz`
    const lvl = document.createElement('span')
    lvl.textContent = `${s.level_dbm.toFixed(0)} dBm`
    // Sinal forte em tom quente, fraco em tom frio — dá pra achar quem está
    // com mais força só de olhar a lista, sem ler o número.
    lvl.style.color = nivelParaCor((s.level_dbm - floor) / DYNAMIC_RANGE)
    li.append(tag, cs, grow, f, lvl)
    list.append(li)
  }
}
